import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SessionStore } from '../app-state/session.store';
import { environment } from '../../environments/environment';

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  sessionStore = inject(SessionStore);
  notification = inject(NzNotificationService);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only handle session-related API calls.
    //
    // The host check is load-bearing, not tidiness. AI scribe recordings are
    // PUT straight to S3 against presigned URLs, and the S3 key embeds the
    // session id — `organization/<org>/sessions/<session>/ai_scribe/...` — so
    // an S3 URL matches the session pattern below. A 403 from S3 means the
    // presigned URL expired, which the upload recovers from by itself, but it
    // would reach the handler below and remove the case from the user's list
    // mid-recording. Only our own API can report session access errors.
    const isSessionApi =
      req.url.startsWith(environment.api_host) &&
      (/\/sessions\/[0-9a-fA-F-]+/.test(req.url) ||
        /[?&]sessionId=/.test(req.url) ||
        req.url.includes('/ai-scribe/') ||
        req.url.includes('/chat-sessions'));

    return next.handle(req).pipe(
      catchError((error: any) => {
        if (isSessionApi && error instanceof HttpErrorResponse) {
          const code = error.error?.errorCode;
          const status = error.status;

          // 403 or specific session access issues
          if (status === 403 && ['USER_NOT_SESSION_MEMBER'].includes(code)) {
            this.handleSessionAccessError(req, code);
          }

          // Optional: handle 404 if session no longer exists
          if (status === 404 && code === 'SESSION_NOT_FOUND') {
            this.handleSessionRemoval(req, 'Case not found.');
          }
        }

        return throwError(() => error);
      }),
    );
  }

  private handleSessionAccessError(req: HttpRequest<any>, code?: string) {
    const sessionId = this.extractSessionId(req.url);

    if (sessionId) {
      this.sessionStore.removeSession(sessionId);

      const selected = this.sessionStore.activeSession();
      if (selected?.id === sessionId) {
        this.sessionStore.clearActiveSession();
      }

      this.notification.create(
        'warning',
        'Access Revoked',
        'You no longer have access to this case. It has been removed from your list.',
        { nzDuration: 5000 },
      );
    }
  }

  private handleSessionRemoval(req: HttpRequest<any>, message: string) {
    const sessionId = this.extractSessionId(req.url);
    if (sessionId) {
      this.sessionStore.removeSession(sessionId);
      this.sessionStore.clearActiveSession();
      this.notification.create('info', 'Case Removed', message, {
        nzDuration: 4000,
      });
    }
  }

  private extractSessionId(url: string): string | null {
    const pathMatch = url.match(/sessions\/([0-9a-fA-F-]+)/);
    if (pathMatch) return pathMatch[1];
    const queryMatch = url.match(/[?&]sessionId=([0-9a-fA-F-]+)/);
    return queryMatch ? queryMatch[1] : null;
  }
}
